import {
  Grid,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  styled,
} from "@mui/material";
import moment from "moment";
import { ReactNode } from "react";

const InfoRow = ({
  label,
  value,
  width,
}: {
  label: ReactNode;
  value: ReactNode;
  width?: string | number;
}) => (
  <TableRow sx={{ border: "none" }}>
    <TableCell
      component="th"
      scope="row"
      className="!lg:py-[2px] !p-0 lg:w-[140px] !w-[100px] !lg:text-[13px] !text-xs"
      sx={{
        border: "none",
        verticalAlign: "top",
      }}
    >
      {label}
    </TableCell>
    <TableCell
      className="lg:py-[2px] !p-0 lg:text-[13px] !text-xs w-[50%]"
      sx={{
        width: width,
        border: "none",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }}
    >
      {label === "E-mail:" ? (
        <a style={{ color: "black" }} href={`mailto: ${value}`}>
          {value}
        </a>
      ) : (
        value
      )}
    </TableCell>
  </TableRow>
);

const anchorStyle = {
  textDecoration: "none",
  display: "inline-block",
  borderBottom: "0.5px solid black",
  paddingBottom: "1px",
};

// The contract body is stored as one blob per language. Keyed rather than a
// ternary so an unrecognised language falls back to English instead of silently
// showing the tenant French.
const COMMENTARY_FIELDS: Record<string, string> = {
  english: "eng_commentaries",
  french: "fr_commentaries",
  dutch: "nl_commentaries",
};

const commentariesFor = (doc: any, language: string): string => {
  const field = COMMENTARY_FIELDS[language] || COMMENTARY_FIELDS.english;
  const raw = doc?.[field];
  return typeof raw === "string" && raw.trim() ? raw : "";
};

function ContractPdf({
  contract,
  annexes = [],
  booking,
  companyInfo,
  sigPad,
  save,
  clear,
  language
}: {
  contract: any
  /**
   * Annexes sent with the lease. One electronic signature covers the lease and
   * its annexes together, so they are part of what the tenant is agreeing to
   * and have to be on screen before the signature is taken. The stored PDF has
   * always contained them; this page used to render the lease alone.
   */
  annexes?: any[];
  booking: any;
  companyInfo: any;
  sigPad: any;
  save: any;
  clear: any;
  language: any;
}) {
  const MultiLayerBorderHeader = styled(Box)(({ theme }) => ({
    position: "relative",
    margin: theme.spacing(2, 0, 3, 0),
    padding: "2px",
    border: "3px solid #000",

    "& .inner-content": {
      padding: theme.spacing(1.5),
      border: "1px solid #000",
      textAlign: "center",
    },
  }));
  const formattedStartDate = moment(booking?.start_date).format(
    "MMMM DD, YYYY"
  );
  console.log("start", moment(booking?.start_date).format(
    "MMMM DD, YYYY"
  ));

  const formattedEndDate = moment(booking?.end_date).format("MMMM DD, YYYY");
  const checkin = moment(booking?.start_date).format("MM/DD/YYYY");
  const checkout = moment(booking?.end_date).format("MM/DD/YYYY");
  const nights = moment(checkout).diff(
    moment(checkin),
    "days"
  );
  const finalString = `From ${formattedStartDate} to ${formattedEndDate} ${nights > 1 ? `(${nights} nights)` : `(${nights} night)`
    } `;
  const formattedCheckIn = moment(
    booking?.property_id?.check_in_time,
    "HH:mm"
  ).format("h:mm a");
  const formattedCheckOut = moment(
    booking?.property_id?.check_out_time,
    "HH:mm"
  ).format("h:mm a");

  const formatNumber = (num: number) => String(Number(num).toFixed(2)).replace(".", ",");
  const total = formatNumber(Number(booking?.cleaning_fees || 0) + Number(booking?.service_fees || 0) + Number(booking?.extra_fees || 0) + Number(booking?.city_tax || 0) + Number(booking?.rent || 0))
  const finalText = `Check in after ${formattedCheckIn} & check out at ${formattedCheckOut}.`;
  return (
    <div>
      <Box
        sx={{
          margin: "auto",
          p: 1,
          borderRadius: 2,
          bgcolor: "white",
          pageBreakInside: "avoid",
          breakInside: "avoid",
        }}
      >
        {/* Header */}
        <MultiLayerBorderHeader>
          <Box className="inner-content">
            <p className="text-xs md:text-sm lg:text-[16px] font-bold uppercase">
              {`${contract?.title} ${booking?.property_id?.reference_no
                } ${moment(booking?.start_date).format("YYMMDD")} ${moment(
                  booking?.end_date
                ).format("YYMMDD")}`}
            </p>
          </Box>
        </MultiLayerBorderHeader>
        <Box my={2}>
          <Grid container spacing={2}>
            <Grid md={6} className="lg:px-[16px] lg:py-[8px] p-[10px]">
              <div>
                <Typography variant="subtitle2" color="gray">
                  THE COMPANY
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">

                  <TableBody>
                    <InfoRow
                      label="Company Name:"
                      value={"EUROPEA Residences"}
                    />
                    <InfoRow label="Business number:" value={"0643510470"} />
                    <InfoRow label="Address:" value={"15 Monts des arts"} />
                    <InfoRow label="Zip code:" value={"1000"} />
                    <InfoRow label="City:" value={"Brussels"} />
                    <InfoRow label="Country:" value={"Belgium"} />
                    <InfoRow label="Manager:" value={"Le Metayer Yann"} />
                    <TableRow sx={{ border: "none" }}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          border: "none",
                          padding: "2px 0",
                          width: "250px",
                          verticalAlign: "top",
                          fontSize: "13px",
                          fontWeight: "bold",
                        }}
                      >
                        {`Hereinafter called ${"The Company"}`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Grid>
            <Grid sx={{ px: 2, py: 1 }} md={6}>
              <div>
                <Typography variant="subtitle2" color="gray">
                  THE TENANT
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Table size="small">
                  <TableBody>
                    {/* <InfoRow
                      label="Title:"
                      value={"Mr"}
                    /> */}
                    <InfoRow label="Name:" value={ <span style={{ textTransform:"capitalize"}}>{booking?.guest_name || ""}</span>} />
                    <InfoRow label="Zip code:" value={""} />
                    <InfoRow label="City:" value={booking?.property_id?.location || ""} />
                    <InfoRow label="Country:" value={booking?.property_id?.country || ""} />
                    <InfoRow label="Language:" value={language} />
                    <InfoRow label="Phone:" value={booking?.guest_phone || ""} />
                    <InfoRow label="E-mail:" value={booking?.guest_email || ""} />

                    <TableRow sx={{ border: "none" }}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          border: "none",
                          padding: "2px 0",
                          width: "250px",
                          verticalAlign: "top",
                          fontSize: "13px",
                          fontWeight: "bold",
                        }}
                      >
                        {`Hereinafter called ${"The Tenant"}`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </Grid>
          </Grid>
          <div
            style={{
              display: "flex",
              fontSize: "10px",
              gap: "312px",
            }}
          >
            {/* <Typography sx={{ fontWeight: "bold" }}>
              Hereinafter called "The Company"
            </Typography> */}
            {/* <Typography sx={{ fontWeight: "bold" }}>
              Hereinafter called "The Tenant"
            </Typography> */}
          </div>
        </Box>

        <Typography
          variant="subtitle2"
          color="gray"
        >{`PARTICULAR TERMS`}</Typography>
        <Divider sx={{ color: "#212121" }} />

        <Box
          sx={{
            // margin: "auto",
            borderRadius: 2,
            bgcolor: "white",
          }}
        >
          <div
            className="lg:text-[14px] text-[12px] font-bold lg:my-[10px] my-[8px]"
            style={{
              color: "rgb(12, 88, 110)",
            }}
          >
            Apartment details
          </div>
          <Typography className="lg:text-[13px] !text-[12px] md:text-[12px] !my-[8px]">
            The Company rents on behalf of the Tenant, who accepts as a furnished rental, the premises described below:
          </Typography>

          <Box>
            <Table size="small">
              <TableBody>
                <InfoRow
                  label="Name:"
                  value={booking?.property_id?.title}
                />
                <InfoRow
                  label="Number of persons:"
                  value={
                    booking?.number_of_person
                      ? (
                        <b>
                          {booking.number_of_person > 1
                            ? `${booking.number_of_person} persons`
                            : `${booking.number_of_person} person`}
                        </b>
                      )
                      : ''
                  }
                />
                <InfoRow
                  label="Surface area:"
                  value={booking?.property_id?.total_size}
                />
                <InfoRow label="Reference:" value={booking?.property_id?.reference_no} />
                <InfoRow
                  label="Address:"
                  value={
                    <>
                      <b>{booking?.property_id?.address}</b>
                    </>
                  }
                />
              </TableBody>
            </Table>
          </Box>
          <div
            className="lg:text-[14px] text-[12px] font-bold lg:my-[10px] my-[8px]"
            style={{
              color: "rgb(12, 88, 110)",
            }}
          >
            Details of your stay and your welcome
          </div>
          <Box>
            <Table size="small">
              <TableBody>
                <InfoRow
                  label="Dates:"
                  value={
                    <>
                      <b>{finalString}</b>
                    </>
                  }
                />
                <InfoRow
                  label="Number of persons:"
                  value={
                    <>
                      {" "}
                      <b>{booking?.number_of_person}</b>{" "}
                    </>
                  }
                />
                <InfoRow label="Check in & out time:" value={finalText} />
              </TableBody>
            </Table>
            <Typography className="lg:text-[13px] !text-[12px] md:text-[12px] !my-[8px]">
              You will receive your check-in instructions from our team 24 hours before your arrival.

            </Typography>
          </Box>
          <div
            className="lg:text-[14px] text-[12px] font-bold lg:my-[10px] my-[8px]"
            style={{
              color: "rgb(12, 88, 110)",
            }}
          >
            Information concerning the payment
          </div>
          <Box>
            <Table size="small">
              <TableBody>
                <InfoRow
                  label={<><b>Total :</b></>}
                  value={
                    `${total} € - ${booking?.status} via ${booking?.channel_name}` +
                    (booking?.payment_date ? ` on ${moment(booking.payment_date).format("DD-MM-YYYY")}` : "")
                  }
                />
                <InfoRow label="Rent:" value={`${booking?.rent} €`} />
                <InfoRow label="Cleaning fees:" value={`${booking?.cleaning_fees} €`} />
                <InfoRow label="City Tax:" value={`${booking?.city_tax} €`} />
                <InfoRow label="Extra fees:" value={`${booking?.extra_fees} €`} />
                <InfoRow label="Service fees:" value={`${booking?.service_fees} €`} />
                <InfoRow
                  label={
                    <>
                      {" "}
                      <b>Payment method :</b>
                    </>
                  }
                  value={
                    booking?.payment_method?.replace(/_/g, ' ').replace(/^./, (char: string) => char.toUpperCase())
                  }
                />
                <InfoRow
                  label={
                    <>
                      {" "}
                      <b>Guarantee Deposit :</b>
                    </>
                  }
                  value={
                    <div>
                      <p>
                        Platform Insurance Host + Tenant RC + Travel Insurance
                      </p>
                      <p>
                        The final occupant remains solely responsible for their
                        actions during the stay, including any damage caused to
                        the property, the building, or third parties
                      </p>
                    </div>
                  }
                />
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ color: "#212121", marginTop: "30px" }} />

      <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }} className="my-6 justify text-xs">

        <h1 className="lg:text-[14px] text-[12px] font-bold lg:my-[10px] my-[8px]" style={{ color: "rgb(12, 88, 110)" }}>RENTAL AGREEMENT TERMS</h1>
        <p className="text-justify">{commentariesFor(contract, language)} </p>

        {/* Same order as the generated PDF: the lease's terms, then one section
            per annex, then the single signature panel below. */}
        {annexes.map((annex, index) => (
          <div key={annex?._id || index}>
            <h1 className="lg:text-[14px] text-[12px] font-bold lg:my-[10px] my-[8px]" style={{ color: "rgb(12, 88, 110)" }}>
              {(annex?.title || "ANNEX").toUpperCase()}
            </h1>
            <p className="text-justify">{commentariesFor(annex, language)} </p>
          </div>
        ))}
      </div>

    </div>
  );
}

export default ContractPdf;
